import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { UseFormReturn } from "react-hook-form";
import { Typography } from "@akuna/ui";
import { buildStayDayDates } from "@/lib/daycare-hotel/stayDays";
import { formatDateBR } from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import type { ReservationFormValues } from "./reservationForm";
import { stayDaySourceFromValues } from "./reservationDates";
import { ReservationRecurrence } from "./ReservationRecurrence";
import { MultiDateField } from "./MultiDateField";

const MODES = ["single", "pick", "repeat"] as const;
type DateMode = (typeof MODES)[number];

function weekdayOf(iso: string) {
  return new Date(`${iso}T12:00:00`).getDay();
}

/** Datas de uma reserva sem plano: um dia, datas soltas ou uma repetição. */
export function ReservationDatesField({ form }: { form: UseFormReturn<ReservationFormValues> }) {
  const { t } = useTranslation("daycareHotel");
  const values = form.watch();
  if (values.moduleType !== "day_care") return null;

  const mode: DateMode = values.dateMode ?? "single";
  const dates = mode === "single" ? [] : buildStayDayDates(stayDaySourceFromValues(values));

  const setMode = (next: DateMode) => {
    form.setValue("dateMode", next, { shouldValidate: true });
    form.setValue("excludedDates", [], { shouldValidate: true });
    form.setValue("pickedDates", next === "pick" ? [values.checkInDate] : [], { shouldValidate: true });
    form.setValue(
      "recurrence",
      next === "repeat"
        ? { frequency: "weekly", interval: 1, weekdays: [weekdayOf(values.checkInDate)], monthday: null, endsOn: "" }
        : null,
      { shouldValidate: true },
    );
  };

  const remove = (date: string) => {
    if (mode === "pick") {
      const next = (values.pickedDates ?? []).filter((item) => item !== date);
      form.setValue("pickedDates", next, { shouldValidate: true });
      if (next[0]) form.setValue("checkInDate", next[0], { shouldValidate: true });
      return;
    }
    form.setValue("excludedDates", [...new Set([...(values.excludedDates ?? []), date])], { shouldValidate: true });
  };

  return (
    <section className="grid gap-3 rounded-xl border border-border bg-card p-4">
      <Typography variant="h5">{t("reservationDates.title")}</Typography>
      <div role="radiogroup" aria-label={t("reservationDates.title")} className="flex flex-wrap gap-1.5">
        {MODES.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={mode === option}
            onClick={() => setMode(option)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              mode === option ? "border-primary bg-primary text-primary-foreground" : "border-border text-foreground hover:bg-muted",
            )}
          >
            {t(`reservationDates.modes.${option}`)}
          </button>
        ))}
      </div>

      {mode === "pick" ? (
        <MultiDateField
          value={values.pickedDates ?? []}
          onChange={(next) => {
            form.setValue("pickedDates", next, { shouldValidate: true });
            if (next[0]) form.setValue("checkInDate", next[0], { shouldValidate: true });
          }}
        />
      ) : null}

      {mode === "repeat" ? (
        <>
          <ReservationRecurrence form={form} showToggle={false} />
          <p className="text-xs text-muted-foreground">{t("reservationDates.planHint")}</p>
        </>
      ) : null}

      {dates.length > 0 ? (
        <div className="grid gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">{t("reservationDates.count", { count: dates.length })}</span>
          <ul aria-label={t("reservationDates.chipsLabel")} className="flex flex-wrap gap-1.5">
            {dates.map((date) => (
              <li key={date}>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted py-1 pl-2.5 pr-1 text-xs font-medium text-foreground">
                  {formatDateBR(date)}
                  <button
                    type="button"
                    aria-label={t("reservationDates.remove", { date: formatDateBR(date) })}
                    onClick={() => remove(date)}
                    className="rounded-full p-0.5 text-muted-foreground hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <X aria-hidden="true" size={12} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
